import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { getImageByIdApiImagesImageIdGetQueryKey } from "../generated/api/@tanstack/react-query.gen";
import {
  expandElementsApiImagesImageIdParametersExpandElementsPost,
  expandGroupParametersApiImagesImageIdParametersExpandGroupPost,
  extractParametersFromImageApiImagesImageIdParametersPost,
} from "../generated/api/sdk.gen";
import { getErrorMessage } from "../lib/api-client";
import type { ProcessingState } from "../lib/parameter-panel-helpers";

function useExtractMutation(
  imageId: string | undefined,
  invalidateImage: () => void,
) {
  return useMutation({
    mutationFn: async () => {
      if (!imageId) throw new Error("No image ID");
      const { data } =
        await extractParametersFromImageApiImagesImageIdParametersPost({
          path: { image_id: imageId },
        });
      return data;
    },
    onSuccess: invalidateImage,
  });
}

function useExpandGroupMutation(
  imageId: string | undefined,
  invalidateImage: () => void,
  setExpandingGroupName: (name: string | null) => void,
) {
  return useMutation({
    mutationFn: async (groupName: string) => {
      if (!imageId) throw new Error("No image ID");
      setExpandingGroupName(groupName);
      const { data } =
        await expandGroupParametersApiImagesImageIdParametersExpandGroupPost({
          path: { image_id: imageId },
          body: { group_name: groupName },
        });
      return data;
    },
    onSuccess: () => {
      invalidateImage();
      setExpandingGroupName(null);
    },
    onError: () => setExpandingGroupName(null),
  });
}

function useExpandElementsMutation(
  imageId: string | undefined,
  invalidateImage: () => void,
) {
  return useMutation({
    mutationFn: async () => {
      if (!imageId) throw new Error("No image ID");
      const { data } =
        await expandElementsApiImagesImageIdParametersExpandElementsPost({
          path: { image_id: imageId },
        });
      return data;
    },
    onSuccess: invalidateImage,
  });
}

export function useParameterMutations(imageId?: string) {
  const queryClient = useQueryClient();
  const [expandingGroupName, setExpandingGroupName] = useState<string | null>(
    null,
  );

  const invalidateImage = () =>
    queryClient.invalidateQueries({
      queryKey: getImageByIdApiImagesImageIdGetQueryKey({
        path: { image_id: imageId ?? "" },
      }),
    });

  const extract = useExtractMutation(imageId, invalidateImage);
  const expandGroup = useExpandGroupMutation(
    imageId,
    invalidateImage,
    setExpandingGroupName,
  );
  const expandElements = useExpandElementsMutation(imageId, invalidateImage);

  return {
    extractParameters: extract.mutate,
    expandGroupParameters: expandGroup.mutate,
    expandElements: expandElements.mutate,
    processing: {
      isExtractingParameters: extract.isPending,
      isExpandingGroup: expandGroup.isPending,
      isExpandingElements: expandElements.isPending,
    } satisfies Partial<ProcessingState>,
    expandingGroupName,
    extractionErrorMessage: extract.error
      ? getErrorMessage(extract.error)
      : null,
    expandGroupErrorMessage: expandGroup.error
      ? getErrorMessage(expandGroup.error)
      : null,
    expandElementsErrorMessage: expandElements.error
      ? getErrorMessage(expandElements.error)
      : null,
  };
}
